import type { ComponentType, ReactNode } from 'react';
import { Badge } from '../../components/ui/badge';

export const NOT_APPLICABLE_LABEL = '해당 없음';
export const NO_DATA_LABEL = '데이터 없음';

/** 사업 지표 하위 패널의 공통 틀(제목·아이콘·본문·하단 주석). */
export function BusinessPanel({
  title,
  testId,
  icon: Icon,
  badge,
  footer,
  children,
}: {
  title: string;
  testId: string;
  icon: ComponentType<{ className?: string }>;
  badge?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const headingId = `${testId}-title`;
  return (
    <section
      data-testid={testId}
      aria-labelledby={headingId}
      className="min-w-0 rounded-lg border bg-card p-4"
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <h3 id={headingId} className="text-sm font-semibold">{title}</h3>
        {badge}
      </div>
      {children}
      {footer ? <p className="mt-3 text-xs text-muted-foreground">{footer}</p> : null}
    </section>
  );
}

/** 블록 null — 이 앱엔 그 지표의 데이터 경로가 없다. */
export function NotApplicableNote({ reason }: { reason: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed p-3 text-sm text-muted-foreground">
      <Badge variant="outline">{NOT_APPLICABLE_LABEL}</Badge>
      <span>{reason}</span>
    </div>
  );
}

/** 지표는 있지만 서버가 데이터를 못 가져왔다 — 서버 문구를 그대로 보인다. */
export function NoDataNote({ message }: { message: string | null }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-amber-300 bg-amber-50/60 p-3 text-sm text-amber-950">
      <Badge variant="warning">{NO_DATA_LABEL}</Badge>
      {message ? <span>{message}</span> : null}
    </div>
  );
}

export function StatTile({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'negative' | 'warning';
}) {
  const toneClass = tone === 'negative'
    ? 'text-destructive'
    : tone === 'warning'
      ? 'text-amber-700'
      : '';
  return (
    <div className="min-w-0 rounded-md border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`mt-1 break-words text-lg font-semibold tabular-nums ${toneClass}`}>{value}</p>
      {hint ? <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function StatGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">{children}</div>;
}
